<template>
  <div button-accept="false" button-cancel="false" :active.sync="isActive">
    <div class="mb-4 flex items-center">
      <vs-button @click="isActive = false" color="dark" type="filled" icon-pack="feather" icon="icon-arrow-left"> Back </vs-button>
    </div>
    <form>
      <div class="vx-row">
        <h3 class="text-xl w-full font-bold my-5">Basic Details</h3>
        <!-- Title -->
        <div class="vx-col w-1/2 mb-3">
          <vs-input
            icon-pack="feather"
            class="w-full"
            v-model="form.title"
            :label="'Donation Title *'"
            v-validate="'required|exclude_special_char'"
            placeholder="Eg. Gurudwara name or cause like child education"
            name="title"
            id="title"
            data-vv-as="Donation Title"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('title')">{{ errors.first('title') }}</span>
        </div>

        <!-- Description -->
        <div class="vx-col w-1/2 mb-2">
          <label class="vs-input--label" for="description">Description</label>
          <vs-textarea
            placeholder="Any notes? Example - taxation details"
            class="w-full mb-0"
            id="description"
            v-validate="'exclude_special_char'"
            name="description"
            v-model="form.description"
            data-vv-as="Description"
            :rows="3"
          />
          <span class="text-danger text-sm" v-show="errors.has('description')">{{ errors.first('description') }}</span>
        </div>

        <vs-divider></vs-divider>

        <h3 class="text-xl w-full font-bold my-5">Donation Image</h3>

        <div class="vx-col w-full cursor-pointer">
          <label class="vs-input--label block"> Image </label>
          <input
            type="file"
            class="border p-2 rounded w-full"
            name="image"
            ref="files"
            accept=".jpg, .png , .jpeg"
            @change="handleFileUpload($event, 'image')"
            style="border: 1px solid rgba(0, 0, 0, 0.2)"
            data-vv-as="Image"
          />
          <span class="text-danger text-sm" v-show="errors.has('image')">{{ errors.first('image') }}</span>
          <div class="mt-5">
            <div class="relative" v-if="image.preview_image">
              <div class="mt-5 overflow-hidden">
                <img height="164px" width="164px" :src="image.preview_image" alt="Image Preview" class="object-fit" />
              </div>
            </div>
          </div>
        </div>
        <vs-divider></vs-divider>

        <h3 class="text-xl w-full font-bold my-5">Qr Code</h3>

        <!-- Qr Image -->
        <div class="vx-col w-full cursor-pointer">
          <label class="vs-input--label block"> Qr Image </label>
          <input
            type="file"
            class="border p-2 rounded w-full"
            name="qr_image"
            ref="files"
            accept=".jpg, .png , .jpeg"
            @change="handleFileUpload($event, 'qr_image')"
            style="border: 1px solid rgba(0, 0, 0, 0.2)"
            data-vv-as="Qr Image"
          />
          <span class="text-danger text-sm block" v-show="errors.has('qr_image')">{{ errors.first('qr_image') }}</span>
          <vs-button type="filled" color="dark" size="large" class="mt-2 text-md" @click="showExampleModal('qr_code')">View Example</vs-button>
          <div class="mt-5">
            <div class="relative" v-if="qr_image.preview_image">
              <div class="mt-5 overflow-hidden">
                <img height="164px" width="164px" :src="qr_image.preview_image" alt="Image Preview" class="object-fit" />
              </div>
            </div>
          </div>
        </div>

        <vs-divider></vs-divider>

        <h3 class="text-xl w-full font-bold my-5">Bank Details</h3>

        <!-- Bank Name -->
        <div class="vx-col w-1/2 mb-3">
          <vs-input
            icon-pack="feather"
            class="w-full"
            v-validate="'exclude_special_char'"
            v-model="form.bank_name"
            :label="'Bank Name'"
            placeholder="Eg. ABCDEFG Bank"
            name="bank_name"
            id="bank_name"
            data-vv-as="Bank Name"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('bank_name')">{{ errors.first('bank_name') }}</span>
        </div>

        <!-- Account Name -->
        <div class="vx-col w-1/2 mb-3">
          <vs-input
            icon-pack="feather"
            class="w-full"
            v-validate="'exclude_special_char'"
            v-model="form.account_name"
            :label="'Account Name'"
            placeholder="Eg. ABCDEFG Bank"
            name="account_name"
            id="account_name"
            data-vv-as="Account Name"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('account_name')">{{ errors.first('account_name') }}</span>
        </div>

        <!-- Account Number -->
        <div class="vx-col w-1/2 mb-3">
          <vs-input
            icon-pack="feather"
            class="w-full"
            v-validate="'exclude_special_char'"
            v-model="form.account_number"
            :label="'Account Number'"
            placeholder="Eg. 1234567890"
            name="account_number"
            id="account_number"
            data-vv-as="Account Number"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('account_number')">{{ errors.first('account_number') }}</span>
        </div>

        <!-- IFSC Code -->
        <div class="vx-col w-1/2 mb-3">
          <vs-input
            icon-pack="feather"
            class="w-full"
            v-validate="'exclude_special_char'"
            v-model="form.ifsc_code"
            :label="'IFSC Code'"
            placeholder="Enter 11 digit IFSC code"
            name="ifsc_code"
            id="ifsc_code"
            :disabled="!isEditable"
            data-vv-as="IFSC Code"
          />
          <span class="text-danger text-sm block" v-show="errors.has('ifsc_code')">{{ errors.first('ifsc_code') }}</span>
          <vs-button type="filled" color="dark" size="large" class="mt-2 text-md" @click="showExampleModal('ifsc_code')">View Example</vs-button>
        </div>

        <!-- IBAN Number -->
        <div class="vx-col w-1/2 mb-3">
          <vs-input
            icon-pack="feather"
            class="w-full"
            v-validate="'exclude_special_char'"
            v-model="form.iban_number"
            :label="'IBAN Number'"
            placeholder="IBAN number is up to 34 characters"
            name="iban_number"
            id="iban_number"
            :disabled="!isEditable"
            data-vv-as="IBAN Number"
          />
          <span class="text-danger text-sm block" v-show="errors.has('iban_number')">{{ errors.first('iban_number') }}</span>
          <vs-button type="filled" color="dark" size="large" class="mt-2 text-md" @click="showExampleModal('iban_number')">View Example</vs-button>
        </div>

        <!-- SWIFT Code -->
        <div class="vx-col w-1/2 mb-3">
          <vs-input
            icon-pack="feather"
            class="w-full"
            v-validate="'exclude_special_char'"
            v-model="form.swift_code"
            :label="'SWIFT Code'"
            placeholder="SWIFT code is up to 11 characters"
            name="swift_code"
            id="swift_code"
            :disabled="!isEditable"
            data-vv-as="SWIFT Code"
          />
          <span class="text-danger text-sm block" v-show="errors.has('swift_code')">{{ errors.first('swift_code') }}</span>
          <vs-button type="filled" color="dark" size="large" class="mt-2 text-md" @click="showExampleModal('swift_code')">View Example</vs-button>
        </div>

        <!-- Branch Address -->
        <div class="vx-col w-1/2 mb-3">
          <vs-input
            icon-pack="feather"
            class="w-full"
            v-validate="'exclude_special_char'"
            v-model="form.branch_address"
            :label="'Branch Address'"
            placeholder="Enter complete branch address"
            name="branch_address"
            id="branch_address"
            :disabled="!isEditable"
            data-vv-as="Branch Address"
          />
          <span class="text-danger text-sm block" v-show="errors.has('branch_address')">{{ errors.first('branch_address') }}</span>
          <vs-button type="filled" color="dark" size="large" class="mt-2 text-md" @click="showExampleModal('branch_address')">View Example</vs-button>
        </div>

        <vs-divider></vs-divider>

        <!-- Mobile Numbers Section -->
        <h3 class="text-xl w-full font-bold my-5">Pay Directly To Phone Number</h3>

        <div v-for="(mobile, index) in form.pay_direct_phone_number" :key="index" class="vx-col w-1/2 px-2 mb-4">
          <div class="flex items-center">
            <div class="w-full">
              <label class="vs-input--label">Mobile Number *</label>
              <vue-tel-input
                v-model="form.pay_direct_phone_number[index].full_number"
                class="w-full vs-inputx vs-input--input"
                placeholder="Enter mobile number"
                v-validate="'required'"
                data-vv-as="Mobile Number"
                :name="'pay_direct_phone_number_' + index"
                :disabled="!isEditable"
                @input="(value, metadata) => updateMobileNumber(index, value, metadata)"
              />
              <span class="text-danger text-sm" v-show="errors.has('pay_direct_phone_number_' + index)">{{ errors.first('pay_direct_phone_number_' + index) }}</span>
            </div>
            <vx-tooltip :text="'Remove Mobile Number'" v-if="isEditable">
              <vs-button @click="removeMobileNumber(index)" class="mt-6 ml-2" color="danger" type="gradient" icon-pack="feather" icon="icon-trash-2" />
            </vx-tooltip>
          </div>
        </div>
        <div class="vx-col w-full block">
          <vs-button @click="addMobileNumber()" color="success" size="default"> Add Mobile Number</vs-button>
        </div>

        <vs-divider></vs-divider>

        <!-- Donation Links Fields Section -->
        <div class="w-full mb-4">
          <div class="flex gap-2 my-5">
            <div class="flex items-center">
              <h3 class="text-xl font-bold">Donation Button Links</h3>
            </div>
            <vs-button @click="addDonationLink()" class="color" size="default" v-if="isEditable" icon-pack="feather" icon="icon-plus"> Add Payment Link</vs-button>
          </div>
          <div v-for="(donationLink, index) in form.donation_link" :key="index" class="donation-link-entry w-full flex flex-wrap mb-8">
            <div class="vx-col w-full md:w-1/2 px-2 mb-8">
              <vs-input
                v-model="donationLink.title"
                label="Link Heading *"
                :name="'donation_link_title_' + index"
                v-validate="'required|exclude_special_char'"
                data-vv-as="Link Heading"
                placeholder="Example - Donate with PayTm"
                class="w-full"
                :disabled="!isEditable"
              />
              <span class="text-danger text-sm" v-show="errors.has(`donation_link_title_${index}`)">{{ errors.first(`donation_link_title_${index}`) }}</span>
            </div>
            <div class="vx-col w-full md:w-1/2 px-2 mb-8">
              <div class="flex items-center">
                <vs-input
                  v-model="donationLink.link"
                  label="Link *"
                  :name="'donation_link_' + index"
                  v-validate="'required|url|exclude_special_char'"
                  data-vv-as="Donation Link"
                  placeholder="Enter your payment link"
                  class="w-full"
                  :disabled="!isEditable"
                />
                <vx-tooltip :text="`Remove Donation Link`" class="ml-2 mt-6">
                  <vs-button @click="removeDonationLink(index)" color="danger" type="gradient" icon-pack="feather" icon="icon-trash-2" />
                </vx-tooltip>
              </div>
              <span class="text-danger text-sm" v-show="errors.has(`donation_link_${index}`)">{{ errors.first(`donation_link_${index}`) }}</span>
            </div>
          </div>
        </div>

        <!-- Save & Cancel Buttons -->
        <div class="vx-row mt-6">
          <div>
            <vs-button class="mr-2 vs-con-loading__container" id="create-contact" @click="save_changes" v-if="isEditable">Save Changes</vs-button>
            <vs-button color="danger" @click="navigateList" class="text-white-500"> Cancel </vs-button>
          </div>
        </div>
      </div>
    </form>
    <vs-popup title="View Example" name="GuideTutorial" fullscreen :active.sync="toggleViewExampleModal" style="z-index: 55000; overflow: hidden">
      <div class="text-center flex justify-center modal-content-body">
        <img :src="viewExampleImage" />
      </div>
    </vs-popup>
  </div>
</template>

<script>
import Select2 from '@/components/custom/form-elements/Select2.vue'
import ibanNumberImage from '@/assets/images/donation/iban-number.png'
import swiftCodeImage from '@/assets/images/donation/swift-code.png'
import qrCodeImage from '@/assets/images/donation/qr-code.png'
import branchAddressImage from '@/assets/images/donation/branch-address.png'
import ifscCodeImage from '@/assets/images/donation/ifsc-code.webp'
import { mapActions, mapGetters, mapState } from 'vuex'
export default {
  /** Component Name */
  name: 'AddUpdateDonation',

  /** Props */
  props: {
    selectedRecordId: String,
    module_name: String,
    action_name: String,
    showModal: Boolean,
    clonedData: Object // NEW PROP FOR CLONED DATA
  },

  components: {
    Select2
  },

  /** Data */
  data() {
    return {
      form: {
        image: null,
        qr_image: null,
        donation_link: [],
        title: '',
        description: '',
        organization: this.$route.params.id,
        account_name: '',
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        iban_number: '',
        swift_code: '',
        branch_address: '',
        pay_direct_phone_number: []
      },
      image: {
        preview_image: ''
      },
      qr_image: {
        preview_image: ''
      },
      viewExampleImage: null,
      toggleViewExampleModal: false,
      isEditing: !!this.selectedRecordId
    }
  },

  /** Computed Properties */
  computed: {
    ...mapState('organization', ['createLoading']),
    ...mapGetters('auth', ['checkPermissionSlug']),
    isEditable() {
      return this.checkPermissionSlug('organization_donation_edit')
    },
    validateForm() {
      return !this.errors.any()
    },
    isActive: {
      get() {
        return this.showModal
      },
      set(val) {
        this.$emit('update:showModal', val)
      }
    }
  },

  /** Lifecycle Hooks */
  async created() {
    if (this.clonedData) {
      this.form = { ...this.clonedData }

      this.form.pay_direct_phone_number = (this.clonedData.pay_direct_phone_number || []).map((mobile) => ({
        country_code: mobile.country_code,
        pay_direct_phone_number: mobile.mobile_number,
        full_number: mobile.country_code ? `+${mobile.country_code} ${mobile.mobile_number}` : mobile.mobile_number
      }))
    } else if (this.selectedRecordId) {
      try {
        const res = await this.$store.dispatch('organization/donationShow', this.selectedRecordId)
        const data = res.data.data

        this.image.preview_image = data.image ? data.image : null
        this.qr_image.preview_image = data.qr_image ? data.qr_image : null

        /* Initialize form with fetched data */
        this.form.title = data.title || ''
        this.form.description = data.description || ''
        this.form.account_name = data.account_name || ''
        this.form.bank_name = data.bank_name || ''
        this.form.account_number = data.account_number || ''
        this.form.ifsc_code = data.ifsc_code || ''
        this.form.iban_number = data.iban_number || ''
        this.form.swift_code = data.swift_code || ''
        this.form.branch_address = data.branch_address || ''
        this.form.donation_link = data.donation_link || []
        this.form.pay_direct_phone_number = (data.pay_direct_phone_number || []).map((mobile) => ({
          country_code: mobile.country_code,
          pay_direct_phone_number: mobile.mobile_number,
          full_number: mobile.country_code ? `+${mobile.country_code} ${mobile.mobile_number}` : mobile.mobile_number
        }))
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: error.message || 'Failed to load donation details.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
      }
    }
  },

  /** Methods */
  methods: {
    ...mapActions('organization', {
      create: 'donationCreate',
      update: 'donationUpdate'
    }),
    addDonationLink() {
      this.form.donation_link.push({ title: '', link: '' })
    },
    showExampleModal(type) {
      this.toggleViewExampleModal = true
      let showImage = null
      switch (type) {
      case 'iban_number':
        showImage = ibanNumberImage
        break
      case 'swift_code':
        showImage = swiftCodeImage
        break
      case 'qr_code':
        showImage = qrCodeImage
        break
      case 'branch_address':
        showImage = branchAddressImage
        break
      case 'ifsc_code':
        showImage = ifscCodeImage
        break
      default:
        break
      }

      this.viewExampleImage = showImage
    },

    removeDonationLink(index) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: `Confirm Delete`,
        text: `Are you sure you want to delete?`,
        accept: () => {
          this.form.donation_link.splice(index, 1)
        },
        acceptText: 'Delete'
      })
    },
    addMobileNumber() {
      this.form.pay_direct_phone_number.push({
        country_code: '',
        pay_direct_phone_number: ''
      })
    },

    updateMobileNumber(index, value, metadata) {
      if (this.form.pay_direct_phone_number[index]) {
        this.form.pay_direct_phone_number[index].pay_direct_phone_number = metadata.nationalNumber
        this.form.pay_direct_phone_number[index].country_code = metadata.countryCallingCode
        this.$set(this.form.pay_direct_phone_number[index], 'full_number', value)
      }
    },

    removeMobileNumber(mobileIndex) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: `Confirm Delete`,
        text: `Are you sure you want to delete?`,
        accept: () => {
          this.form.pay_direct_phone_number.splice(mobileIndex, 1)
        },
        acceptText: 'Delete'
      })
    },

    navigateList() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: `Confirm Cancel`,
        text: `Are you sure you want to cancel?`,
        accept: () => {
          this.isActive = false
        },
        acceptText: 'Yes',
        cancelText: 'No'
      })
    },
    handleFileUpload(e, fileType) {
      this.contentType = fileType
      const file = e.target.files[0]

      // Check if a file is selected
      if (!file) {
        this.$vs.notify({
          title: 'Error',
          text: 'No file selected',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
        return
      }

      const blob = URL.createObjectURL(file)

      switch (fileType) {
      case 'image':
        this.form.image = file
        this.image.preview_image = blob
        break
      case 'qr_image':
        this.form.qr_image = file
        this.qr_image.preview_image = blob
        break
      default:
        console.warn('Unknown thumbnail type:', fileType)
      }
    },
    /**
     * Save Changes Handler
     */
    async save_changes() {
      // Validate the entire form
      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please fix the errors first.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
        return
      }

      const data = new FormData()
      for (const key in this.form) {
        if (this.form[key] !== null) {
          if (typeof this.form[key] === 'object' && !['image', 'qr_image'].includes(key)) {
            if (key === 'pay_direct_phone_number') {
              this.form.pay_direct_phone_number.forEach((item, index) => {
                data.append(`pay_direct_phone_number[${index}][country_code]`, item.country_code || '')
                data.append(`pay_direct_phone_number[${index}][mobile_number]`, item.pay_direct_phone_number || '')
              })
            } else if (key === 'donation_link') {
              this.form.donation_link.forEach((item, index) => {
                data.append(`donation_link[${index}][title]`, item.title || '')
                data.append(`donation_link[${index}][link]`, item.link || '')
              })
            } else {
              this.form[key].forEach((item) => {
                data.append(`${key}[]`, item)
              })
            }
          } else {
            // Handle other types
            data.append(key, this.form[key])
          }
        }
      }

      if (this.isEditing) {
        try {
          // Update existing Donation
          const response = await this.update({
            id: this.selectedRecordId,
            data
          })

          // Close modal and notify success
          this.isActive = false
          this.$emit('update-data', true)
          this.$vs.notify({
            title: 'Success',
            text: response.message || 'Donation updated successfully.',
            iconPack: 'feather',
            icon: 'icon-check',
            position: 'top-center',
            time: 5000,
            color: 'success'
          })
        } catch (error) {
          // Notify error
          this.$vs.notify({
            title: 'Error',
            text: error.message || 'An error occurred while updating.',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            position: 'top-center',
            time: 5000,
            color: 'primary'
          })
        }
      } else {
        try {
          // Create new Donation
          const response = await this.create(data)

          // Close modal and notify success
          this.isActive = false
          this.$emit('update-data', true)
          this.$vs.notify({
            title: 'Success',
            text: response.message || 'Donation created successfully.',
            iconPack: 'feather',
            icon: 'icon-check',
            position: 'top-center',
            time: 5000,
            color: 'success'
          })
        } catch (error) {
          // Notify error
          this.$vs.notify({
            title: 'Error',
            text: error.message || 'An error occurred while creating.',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            position: 'top-center',
            time: 5000,
            color: 'primary'
          })
        }
      }
    }
  },

  /** Watchers */
  watch: {
    createLoading(newVal) {
      if (newVal) {
        this.$vs.loading({
          container: '#create-donation',
          scale: 0.45
        })
      } else {
        this.$vs.loading.close('#create-donation > .con-vs-loading')
      }
    }
  }
}
</script>
